import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import * as bcrypt from 'bcryptjs';
import { environment } from '../../../../environments/environment';
import { UserRepository } from '@domain/user/user.repository';
import { User } from '@domain/user/user';
import { UserMapper } from '@infrastructure/api/mappers/user.mapper';
import { UserResponseDto } from '@infrastructure/api/dtos/user/user.response.dto';

const BCRYPT_SALT_ROUNDS = 10;

@Injectable({ providedIn: 'root' })
export class UserHttpRepository extends UserRepository {
  private readonly url = `${environment.apiUrl}/users`;

  constructor(private readonly http: HttpClient) {
    super();
  }

  getAll(): Observable<User[]> {
    return this.http.get<UserResponseDto[]>(this.url).pipe(
      map(UserMapper.toDomainList)
    );
  }

  create(user: User): Observable<User> {
    return this.hashPassword(user).pipe(
      switchMap(dto => this.http.post<UserResponseDto>(this.url, dto)),
      map(UserMapper.toDomain)
    );
  }

  update(user: User): Observable<User> {
    return this.hashPassword(user).pipe(
      switchMap(dto => this.http.put<UserResponseDto>(`${this.url}/${user.id}`, dto)),
      map(UserMapper.toDomain)
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.url}/${id}`);
  }

  private hashPassword(user: User): Observable<ReturnType<typeof UserMapper.toRequestDto>> {
    const dto = UserMapper.toRequestDto(user);
    if (!dto.password || dto.password.trim() === '') {
      return of(dto);
    }
    return from(bcrypt.hash(dto.password, BCRYPT_SALT_ROUNDS)).pipe(
      map(hashed => ({ ...dto, password: hashed }))
    );
  }
}
